import React from 'react'
import {Flex, SimpleGrid, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import ProductRegistrationHeader from './partials/product-registration-header'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import routingObject from '../../utils/static-routes'
import {useLocation} from 'react-router'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'
import {useProductRegistrationPageMeta} from '../../hooks/use-category-meta'
import {useProductRegistrationData} from '../../custom-hooks/use-product-registration-data'
import ProductRegistrationCategoryImageContainer from './partials/product-registration-category-image-container'
import ProductRegistrationBenefitCard from './partials/product-registration-benefit-card'

const ProductRegistration = () => {
    const styles = useMultiStyleConfig('ProductRegistration')
    const navigate = useNavigation()

    const location = useLocation()
    const {customParams} = getConfig()
    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const {metaTitle, metaDescription} = useProductRegistrationPageMeta()

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: metaTitle,
        description: metaDescription,
        url: currentPath,
    }

    const {categories, cardsData} = useProductRegistrationData()

    const handleCategoryClick = (category) => {
        const categoryPath = category.categoryPath
        localStorage.setItem('productRegistrationSearchCategory', categoryPath)
        localStorage.removeItem('prepopulatedSerialNumber')
        const categoryQuery = `?category=${encodeURIComponent(category?.label)}`
        navigate(`${routingObject.PRODUCT_REGISTRATION_PATH}/search${categoryQuery}`)
    }

    return (
        <Flex {...styles.container} layerStyle={'page'}>
            <Helmet>
                {metaTitle && <title>{metaTitle}</title>}
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            <ProductRegistrationHeader />

            {/* Main content: two horizontal sections */}
            <Flex
                direction={['column', 'column', 'column', 'row']}
                gap={['40px', '40px', '40px', '160px']}
                alignSelf="stretch"
                alignItems="flex-start"
            >
                {/* Left section: Search + Category cards */}
                <Flex direction="column" gap="16px" flex="1" alignSelf="stretch">
                    {/* Category section title */}
                    <Text textStyle="head-small" fontWeight="medium" color="text.default">
                        <FormattedMessage
                            id="product_registration.select_category_title"
                            defaultMessage="Which product would you like to register?"
                        />
                    </Text>

                    {/* Category cards */}
                    <Flex direction="column" gap="12px" alignSelf="stretch">
                        {categories.map((category) => (
                            <ProductRegistrationCategoryImageContainer
                                key={category.id}
                                image={category.image}
                                title={category.label}
                                id={category.id}
                                handleCategoryClick={() => handleCategoryClick(category)}
                            />
                        ))}
                    </Flex>
                </Flex>

                {/* Right section: Benefits */}
                <Flex
                    direction="column"
                    gap={['16px', '16px', '16px', '24px']}
                    flex="1"
                    alignSelf="stretch"
                >
                    <Text textStyle="body-large" fontWeight="regular" color="text.default">
                        <FormattedMessage
                            id="product_registration.why_register"
                            defaultMessage="Why register your product?"
                        />
                    </Text>
                    <SimpleGrid columns={{base: 1, md: 2}} spacing={4} alignSelf="stretch">
                        {cardsData.map((card, index) => (
                            <ProductRegistrationBenefitCard
                                key={index}
                                icon={card.icon}
                                title={card.title}
                                description={card.description}
                                datatestid={card.testId}
                                variant="landing"
                            />
                        ))}
                    </SimpleGrid>
                </Flex>
            </Flex>
        </Flex>
    )
}

export default ProductRegistration
